import { useRef, useCallback, useState, useEffect } from 'react';
import * as Tone from 'tone';
import { useGameStore } from '@/hooks/useGameState';

export const useAudioManager = () => {
  const isMuted = useGameStore((state) => state.isMuted);
  const [isInitialized, setIsInitialized] = useState(false);

  // SFX synths
  const synthRef = useRef<Tone.PolySynth | null>(null);
  const noiseRef = useRef<Tone.Noise | null>(null);
  const volumeRef = useRef<Tone.Volume | null>(null);

  // BGM layers
  const padSynthRef = useRef<Tone.PolySynth | null>(null);
  const arpeggioSynthRef = useRef<Tone.PolySynth | null>(null);
  const bassSynthRef = useRef<Tone.MonoSynth | null>(null);
  const kickSynthRef = useRef<Tone.MembraneSynth | null>(null);

  // BGM sequences
  const padSequenceRef = useRef<Tone.Sequence | null>(null);
  const arpeggioSequenceRef = useRef<Tone.Sequence | null>(null);
  const bassSequenceRef = useRef<Tone.Sequence | null>(null);
  const kickSequenceRef = useRef<Tone.Sequence | null>(null);

  const initialize = useCallback(async () => {
    if (isInitialized) return;

    await Tone.start();

    // Master volume
    volumeRef.current = new Tone.Volume(-8).toDestination();

    // ========================================
    // SFX SYNTHS
    // ========================================

    // SFX synth (polyphonic for collect sounds)
    synthRef.current = new Tone.PolySynth(Tone.Synth, {
      oscillator: { type: 'sine' },
      envelope: {
        attack: 0.01,
        decay: 0.1,
        sustain: 0.1,
        release: 0.3,
      },
    }).connect(volumeRef.current);

    // Noise for boost sound (white noise through lowpass filter)
    const noiseFilter = new Tone.Filter(2000, 'lowpass').connect(
      new Tone.Volume(-20).connect(volumeRef.current)
    );
    noiseRef.current = new Tone.Noise('white').connect(noiseFilter);

    // ========================================
    // BGM LAYER 1 - PAD (Atmosphere)
    // Slow attack/release polysynth for sustained chords
    // ========================================
    const padVolume = new Tone.Volume(-18).connect(volumeRef.current);
    const padReverb = new Tone.Reverb({ decay: 4, wet: 0.5 }).connect(padVolume);

    padSynthRef.current = new Tone.PolySynth(Tone.Synth, {
      oscillator: { type: 'sine' },
      envelope: {
        attack: 1.5,
        decay: 0.5,
        sustain: 0.8,
        release: 2.0,
      },
    }).connect(padReverb);

    // Chord progression: Cm -> Fm -> Gm -> Ab (synthwave classic)
    const padChords = [
      ['C3', 'Eb3', 'G3'],   // Cm
      ['F3', 'Ab3', 'C4'],   // Fm
      ['G3', 'Bb3', 'D4'],   // Gm
      ['Ab3', 'C4', 'Eb4'],  // Ab
    ];

    padSequenceRef.current = new Tone.Sequence(
      (time, chord) => {
        if (padSynthRef.current && !isMuted) {
          padSynthRef.current.triggerAttackRelease(chord, '2n', time);
        }
      },
      padChords,
      '1n' // One chord per bar
    );
    padSequenceRef.current.loop = true;

    // ========================================
    // BGM LAYER 2 - ARPEGGIO (Melody)
    // Fast arpeggiated pattern in higher octave
    // ========================================
    const arpVolume = new Tone.Volume(-14).connect(volumeRef.current);
    const arpDelay = new Tone.PingPongDelay('8n', 0.2).connect(arpVolume);

    arpeggioSynthRef.current = new Tone.PolySynth(Tone.Synth, {
      oscillator: { type: 'sawtooth' },
      envelope: {
        attack: 0.01,
        decay: 0.15,
        sustain: 0.2,
        release: 0.3,
      },
    }).connect(new Tone.Filter(3000, 'lowpass').connect(arpDelay));

    // Arpeggio notes following chord progression (16th notes)
    // Each sub-array is one bar, played as 16th notes
    const arpeggioPattern = [
      // Bar 1: Cm arpeggio
      'C5', 'Eb5', 'G5', 'C6', 'G5', 'Eb5', 'C5', 'G4',
      'C5', 'Eb5', 'G5', 'C6', 'G5', 'Eb5', 'C5', 'Eb5',
      // Bar 2: Fm arpeggio
      'F5', 'Ab5', 'C6', 'F6', 'C6', 'Ab5', 'F5', 'C5',
      'F5', 'Ab5', 'C6', 'F6', 'C6', 'Ab5', 'F5', 'Ab5',
      // Bar 3: Gm arpeggio
      'G5', 'Bb5', 'D6', 'G6', 'D6', 'Bb5', 'G5', 'D5',
      'G5', 'Bb5', 'D6', 'G6', 'D6', 'Bb5', 'G5', 'Bb5',
      // Bar 4: Ab arpeggio
      'Ab5', 'C6', 'Eb6', 'Ab6', 'Eb6', 'C6', 'Ab5', 'Eb5',
      'Ab5', 'C6', 'Eb6', 'Ab6', 'Eb6', 'C6', 'Ab5', 'C6',
    ];

    arpeggioSequenceRef.current = new Tone.Sequence(
      (time, note) => {
        if (arpeggioSynthRef.current && !isMuted) {
          arpeggioSynthRef.current.triggerAttackRelease(note, '16n', time);
        }
      },
      arpeggioPattern,
      '16n'
    );
    arpeggioSequenceRef.current.loop = true;

    // ========================================
    // BGM LAYER 3 - BASS (Foundation)
    // Root notes following chord progression, longer notes
    // ========================================
    const bassVolume = new Tone.Volume(-10).connect(volumeRef.current);
    const bassFilter = new Tone.Filter(400, 'lowpass').connect(bassVolume);

    bassSynthRef.current = new Tone.MonoSynth({
      oscillator: { type: 'sawtooth' },
      envelope: {
        attack: 0.05,
        decay: 0.2,
        sustain: 0.6,
        release: 0.4,
      },
    }).connect(bassFilter);

    // Bass follows root notes (half notes)
    const bassPattern = [
      'C2', null, 'C2', null,  // Cm bar
      'F2', null, 'F2', null,  // Fm bar
      'G2', null, 'G2', null,  // Gm bar
      'Ab2', null, 'Ab2', null, // Ab bar
    ];

    bassSequenceRef.current = new Tone.Sequence(
      (time, note) => {
        if (bassSynthRef.current && !isMuted && note) {
          bassSynthRef.current.triggerAttackRelease(note, '4n', time);
        }
      },
      bassPattern,
      '4n'
    );
    bassSequenceRef.current.loop = true;

    // ========================================
    // BGM LAYER 4 - KICK (Subtle pulse)
    // MembraneSynth for deep kick, every beat
    // ========================================
    const kickVolume = new Tone.Volume(-16).connect(volumeRef.current);

    kickSynthRef.current = new Tone.MembraneSynth({
      pitchDecay: 0.05,
      octaves: 4,
      oscillator: { type: 'sine' },
      envelope: {
        attack: 0.001,
        decay: 0.2,
        sustain: 0,
        release: 0.4,
      },
    }).connect(kickVolume);

    // Kick on every beat (quarter notes)
    const kickPattern = ['C1', 'C1', 'C1', 'C1'];

    kickSequenceRef.current = new Tone.Sequence(
      (time, note) => {
        if (kickSynthRef.current && !isMuted) {
          kickSynthRef.current.triggerAttackRelease(note, '8n', time);
        }
      },
      kickPattern,
      '4n'
    );
    kickSequenceRef.current.loop = true;

    setIsInitialized(true);
  }, [isInitialized, isMuted]);

  const startBGM = useCallback(() => {
    if (!isInitialized) return;

    // Set BPM (synthwave typically 100-120)
    Tone.Transport.bpm.value = 110;
    Tone.Transport.start();

    // Start all BGM layers
    padSequenceRef.current?.start(0);
    arpeggioSequenceRef.current?.start(0);
    bassSequenceRef.current?.start(0);
    kickSequenceRef.current?.start(0);
  }, [isInitialized]);

  const stopBGM = useCallback(() => {
    padSequenceRef.current?.stop();
    arpeggioSequenceRef.current?.stop();
    bassSequenceRef.current?.stop();
    kickSequenceRef.current?.stop();
    Tone.Transport.stop();
  }, []);

  const playEatSound = useCallback(() => {
    if (!synthRef.current || isMuted) return;

    // Random note from C major scale (high pitched ping)
    const notes = ['C5', 'D5', 'E5', 'F5', 'G5', 'A5', 'B5', 'C6'];
    const note = notes[Math.floor(Math.random() * notes.length)];
    synthRef.current.triggerAttackRelease(note, '16n');
  }, [isMuted]);

  const playDeathSound = useCallback(() => {
    if (!synthRef.current || isMuted) return;

    // Descending notes for death
    const notes = ['C3', 'B2', 'A2', 'G2', 'F2', 'E2'];
    let delay = 0;
    notes.forEach((note) => {
      setTimeout(() => {
        if (synthRef.current) {
          synthRef.current.triggerAttackRelease(note, '8n');
        }
      }, delay);
      delay += 80;
    });
  }, [isMuted]);

  const playBoostSound = useCallback((start: boolean) => {
    if (!noiseRef.current || isMuted) return;

    if (start) {
      noiseRef.current.start();
    } else {
      noiseRef.current.stop();
    }
  }, [isMuted]);

  const playKillSound = useCallback(() => {
    if (!synthRef.current || isMuted) return;

    // Triumphant chord
    const chord = ['C4', 'E4', 'G4', 'C5'];
    synthRef.current.triggerAttackRelease(chord, '4n');
  }, [isMuted]);

  const playCometSound = useCallback(() => {
    if (!synthRef.current || isMuted) return;

    // Ascending arpeggio for comet catch
    const notes = ['C5', 'E5', 'G5', 'C6', 'E6', 'G6'];
    let delay = 0;
    notes.forEach((note) => {
      setTimeout(() => {
        if (synthRef.current) {
          synthRef.current.triggerAttackRelease(note, '16n');
        }
      }, delay);
      delay += 60;
    });
  }, [isMuted]);

  // Cleanup
  useEffect(() => {
    return () => {
      stopBGM();
      // Dispose SFX
      synthRef.current?.dispose();
      noiseRef.current?.dispose();
      volumeRef.current?.dispose();
      // Dispose BGM synths
      padSynthRef.current?.dispose();
      arpeggioSynthRef.current?.dispose();
      bassSynthRef.current?.dispose();
      kickSynthRef.current?.dispose();
    };
  }, [stopBGM]);

  // Handle mute changes
  useEffect(() => {
    if (volumeRef.current) {
      volumeRef.current.mute = isMuted;
    }
    // Stop boost noise when muted
    if (isMuted && noiseRef.current) {
      noiseRef.current.stop();
    }
  }, [isMuted]);

  return {
    initialize,
    startBGM,
    stopBGM,
    playEatSound,
    playDeathSound,
    playBoostSound,
    playKillSound,
    playCometSound,
    isInitialized,
  };
};
